import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { getIntegrationProviderDisplayName } from '../../../models/utils/integrationUtils';
import { IProviderSetupCounts, ISetupIncompleteAccount } from '../../../queries/Admin/IntegrationsHealth';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill';
import { CollapsibleProviderSection } from './CollapsibleProviderSection';
import { SetupRequirements } from './SetupRequirements';
import { setupUrl } from './integrationUrls';
import { styleSheet } from './styles';

export interface IFilteredSetupIncompleteProvider extends IProviderSetupCounts {
	accounts: ISetupIncompleteAccount[];
	visibleSetupIncompleteCount: number;
}

interface IProps {
	providers: IFilteredSetupIncompleteProvider[];
	expandGroups: boolean;
	isSearching: boolean;
}

/** Share, not count: the raw number tracks how many customers a connector has, the proportion tracks whether its setup flow works. */
export function sortBySetupIncompleteShare<T extends IProviderSetupCounts>(providers: T[]): T[] {
	return [...providers].sort(
		(a, b) => setupIncompleteShare(b) - setupIncompleteShare(a) || b.setupIncompleteCount - a.setupIncompleteCount
	);
}

/** enabledCount already counts the incomplete ones, so it is the denominator rather than a term of it. */
export function setupIncompleteShare(provider: IProviderSetupCounts): number {
	return provider.enabledCount > 0 ? provider.setupIncompleteCount / provider.enabledCount : 0;
}

export const SetupIncompleteSection: React.FC<IProps> = ({ providers, expandGroups, isSearching }) => (
	<>
		{sortBySetupIncompleteShare(providers).map(provider => (
			<CollapsibleProviderSection
				key={provider.provider}
				title={getIntegrationProviderDisplayName(provider.provider)}
				defaultOpen={expandGroups}
				statusChip={
					<>
						{provider.enabledCount > 0 && !isSearching ? (
							<span className={css(styleSheet.providerRatio)}>
								{provider.setupIncompleteCount} of {provider.enabledCount} ·{' '}
								{Math.round(setupIncompleteShare(provider) * 100)}%
							</span>
						) : null}
						<Pill variant='tag' color='orange' label={`${provider.visibleSetupIncompleteCount}`} size='small' />
					</>
				}
			>
				<table className={css(styleSheet.table)}>
					<colgroup>
						<col className={css(styleSheet.colAccount)} />
						<col className={css(styleSheet.colError)} />
						<col className={css(styleSheet.colSuspended)} />
						<col className={css(styleSheet.colActions)} />
					</colgroup>
					<thead>
						<tr>
							<th className={css(styleSheet.th)}>Account</th>
							<th className={css(styleSheet.th)}>Missing</th>
							<th className={css(styleSheet.th)}>Created</th>
							<th className={css(styleSheet.th)}>Actions</th>
						</tr>
					</thead>
					<tbody>
						{provider.accounts.length === 0 ? (
							<tr>
								<td className={css(styleSheet.td, styleSheet.mutedText)} colSpan={4}>
									{provider.visibleSetupIncompleteCount} awaiting configuration, but the account list wasn&apos;t
									returned.
								</td>
							</tr>
						) : null}
						{provider.accounts.map(account => {
							const url = setupUrl(account.accountId, account.provider);
							return (
								<tr key={`${account.accountId}-${account.provider}`}>
									<td className={css(styleSheet.td)}>
										<Link className={css(styleSheet.accountLink)} to={url}>
											{account.companyName ?? account.accountId}
										</Link>
									</td>
									<td className={css(styleSheet.td)}>
										<SetupRequirements requirements={account.requirements} />
									</td>
									<td className={css(styleSheet.td, styleSheet.mutedText)}>
										{account.createdAt ? moment(account.createdAt).fromNow() : '—'}
									</td>
									<td className={css(styleSheet.td)}>
										<Button asChild variant='secondary' size='sm' styles={[styleSheet.actionButton]}>
											<Link to={url}>Open settings</Link>
										</Button>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</CollapsibleProviderSection>
		))}
	</>
);
